import { fetchLabModalPopups } from 'actions/SystemActions'
import { isWaitingForConsentDecision } from '../../../consent/waitingForConsent'
import { ModalStackIdleContext, TryShowModalContext } from 'contexts/ModalProvider'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { useLabModalPopupsRequestContext } from 'hooks/useLabRouteRequestContext'
import { getFirstEligibleLabItemByRank } from 'lib/lab/rankedLabItems'
import { REBRAND_PALETTE_CLASS } from 'lib/theme/paletteVersion'
import Label from 'LuxKit/Label'
import Modal from 'LuxKit/Modal/Modal'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { rem } from 'polished'
import type React from 'react'
import { useContext, useEffect, useMemo } from 'react'
import { recordModalPopupSeen, isModalPopupSuppressed, hasPresentedLabModalThisSession } from 'storage/modalPopup'
import styled from 'styled-components'
import { labModalPopupEvent } from 'analytics/snowplow/events/labModalPopupEvents'

type LabModalPopupStyle = 'light' | 'dark'

export const LAB_MODAL_POPUP_DARK_CLASS_NAME = 'lab-modal-popup-dark'

export const StyledModal = styled(Modal)`
  .is-overlay .appended-action-area button {
    --button-color: var(--button-hover-color);
    --button-background-color: var(--button-hover-background-color);
    --button-border-color: var(--button-hover-border-color, transparent);
    --button-focus-color: var(--button-hover-color);
    --button-focus-background-color: var(--button-hover-background-color);
    --button-focus-border-color: var(--button-hover-border-color, transparent);
    --button-active-color: var(--button-hover-color);
    --button-active-background-color: var(--button-hover-background-color);
    --button-active-border-color: var(--button-hover-border-color, transparent);

    /* Rebrand: use opaque dark primary instead of tertiary hover (translucent). */
    body.${REBRAND_PALETTE_CLASS} & {
      --button-internal-default-color: var(--palette-neutral-surface-normal-contrast);
      --button-internal-default-background-color: var(--palette-neutral-surface-normal-background);
      --button-internal-default-border-color: var(--button-transparent);
      --button-internal-focus-color: var(--palette-neutral-surface-normal-contrast);
      --button-internal-focus-background-color: var(--palette-neutral-surface-normal-background);
      --button-internal-focus-border-color: var(--button-transparent);
      --button-internal-hover-color: var(--palette-neutral-surface-normal-contrast);
      --button-internal-hover-background-color: color-mix(in srgb, var(--palette-neutral-surface-normal-background), var(--palette-neutral-control-color-black) var(--palette-neutral-control-opacity-8));
      --button-internal-hover-border-color: var(--button-transparent);
      --button-internal-active-color: var(--palette-neutral-surface-normal-contrast);
      --button-internal-active-background-color: color-mix(in srgb, var(--palette-neutral-surface-normal-background), var(--palette-neutral-control-color-black) var(--palette-neutral-control-opacity-16));
      --button-internal-active-border-color: var(--button-transparent);
    }
  }

  &.${LAB_MODAL_POPUP_DARK_CLASS_NAME} {
    background-color: ${props => props.theme.palette.neutral.control.black100};
    color: ${props => props.theme.palette.neutral.surface.staticWhite};

    .is-overlay,
    footer {
      background-color: ${props => props.theme.palette.neutral.control.black100};
      color: ${props => props.theme.palette.neutral.surface.staticWhite};
    }

    .is-overlay {
      background-color: transparent;
    }

    footer .kind-tertiary,
    .is-overlay .prepended-action-area button {
      color: ${props => props.theme.palette.neutral.surface.staticWhite};
    }

    footer .kind-primary {
      --color-primary-bg: ${props => props.theme.palette.neutral.surface.staticWhite};
      --color-primary-fg: ${props => props.theme.palette.neutral.surface.lightContrast};
      --color-primary-hover: ${props => props.theme.palette.neutral.surface.lightBackgroundAlt};
      --color-primary-active: ${props => props.theme.palette.neutral.surface.lightForeground};

      body.${REBRAND_PALETTE_CLASS} & {
        --button-background: var(--palette-neutral-surface-static-white);
        --button-contrast: var(--palette-neutral-surface-light-contrast);
        --button-internal-hover-background-color: var(--palette-neutral-surface-light-background-alt);
        --button-internal-hover-border-color: var(--palette-neutral-surface-light-background-alt);
        --button-internal-active-background-color: var(--palette-neutral-surface-light-foreground);
        --button-internal-active-border-color: var(--palette-neutral-surface-light-foreground);
      }
    }

    footer .kind-tertiary:hover {
      background-color: ${props => props.theme.palette.neutral.surface.darkBackground};
      color: ${props => props.theme.palette.neutral.surface.staticWhite};
    }
  }
`

const ContentStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(12)};
`

const getModalPopupStyle = (popup: App.LabModalPopupResult): LabModalPopupStyle => {
  return popup.style === 'dark' ? 'dark' : 'light'
}

function getRenderableModalPopup(modalPopups: App.LabModalPopupsResult | undefined): App.LabModalPopupResult | undefined {
  if (hasPresentedLabModalThisSession()) {
    return undefined
  }

  return getFirstEligibleLabItemByRank(modalPopups?.popups, popup => !isModalPopupSuppressed(popup))
}

export default function ModalPopupsGate() {
  const dispatch = useAppDispatch()
  const tryShowModal = useContext(TryShowModalContext)
  const isModalStackIdle = useContext(ModalStackIdleContext)
  const { currentPath, distinctId, token, labRequestContextKey } = useLabModalPopupsRequestContext()
  const modalPopups = useAppSelector(state => state.system.features.modalPopups)
  const modalPopupsRequestPath = useAppSelector(state => state.system.features.modalPopupsRequestPath)
  const isWaitingForConsent = isWaitingForConsentDecision()

  useEffect(() => {
    if (isWaitingForConsent) return
    dispatch(fetchLabModalPopups(currentPath, distinctId, token))
  }, [dispatch, currentPath, distinctId, token, labRequestContextKey, isWaitingForConsent])

  const popup = useMemo(() => {
    if (isWaitingForConsent || !isModalStackIdle) {
      return undefined
    }
    if (modalPopupsRequestPath && modalPopupsRequestPath !== currentPath) {
      return undefined
    }
    return getRenderableModalPopup(modalPopups)
  }, [isWaitingForConsent, isModalStackIdle, modalPopups, modalPopupsRequestPath, currentPath])

  useEffect(() => {
    if (!isModalStackIdle || !popup || hasPresentedLabModalThisSession()) {
      return
    }

    const content = popup.content
    const secondaryCta = content.primaryCta ? content.secondaryCta : undefined
    const modalStyle = getModalPopupStyle(popup)
    const modalTitle = content.title.trim()
    const modalTagText = content.tag?.text.trim()
    const modalTagVariant = content.tag?.variant
    const modalDescription = content.description?.trim()
    const hasTag = !!(modalTagText && modalTagVariant)
    const hasContent = !!(hasTag || modalTitle || modalDescription)
    let ctaClicked = false

    const modalPromise = tryShowModal(() => {
      recordModalPopupSeen(popup)
      labModalPopupEvent({ popup, action: 'impression' }).catch(() => undefined)

      const handlePrimaryActionClick = (_event: React.MouseEvent<HTMLButtonElement>, resolve: (result?: unknown) => void) => {
        labModalPopupEvent({ popup, action: 'primary_cta_click' }).catch(() => undefined)
        ctaClicked = true
        resolve()
      }

      const handleSecondaryActionClick = (_event: React.MouseEvent<HTMLButtonElement>, resolve: (result?: unknown) => void) => {
        labModalPopupEvent({ popup, action: 'secondary_cta_click' }).catch(() => undefined)
        ctaClicked = true
        resolve()
      }

      const modalContent = hasContent ? (
        <ContentStack>
          {hasTag ? <Label kind="primary" variant={modalTagVariant}>{modalTagText}</Label> : null}
          {modalTitle ? <Heading variant="heading3">{modalTitle}</Heading> : null}
          {modalDescription ? <BodyText variant="large" wrap="pre-wrap">{modalDescription}</BodyText> : null}
        </ContentStack>
      ) : null

      return (
        <StyledModal
          className={modalStyle === 'dark' ? LAB_MODAL_POPUP_DARK_CLASS_NAME : undefined}
          title=""
          isHeaderOverlay
          image={content.imageId ? { id: content.imageId, title: modalTitle || popup.name } : undefined}
          imageParams={content.imageId ? { mobileAspectRatio: '2:1', tabletAspectRatio: '2:1', desktopAspectRatio: '2:1' } : undefined}
          primaryActionText={content.primaryCta?.label}
          primaryActionTo={content.primaryCta?.path}
          secondaryActionText={secondaryCta?.label}
          secondaryActionTo={secondaryCta?.path}
          onPrimaryActionClick={handlePrimaryActionClick}
          onSecondaryActionClick={handleSecondaryActionClick}
        >
          {modalContent}
        </StyledModal>
      )
    })

    if (!modalPromise) {
      return
    }

    modalPromise.then(() => {
      if (!ctaClicked) {
        labModalPopupEvent({ popup, action: 'dismiss' }).catch(() => undefined)
      }
    })
  }, [isModalStackIdle, popup, tryShowModal])

  return null
}
